import React, { useEffect, useContext } from "react";
import "./BillingClient.scss";
import { FormattedMessage } from "react-intl";
import { Typography } from "core/typography/Typography";
import { BILLING_CLIENT_FORM_LABELS } from "constants/forms";
import { getFormattedAuxilaryCode } from "utils/functions";
import {
  renderCompanyName,
  renderSiret,
  renderSiren,
  renderAuxiliaryCode,
  renderInputNationalityIds,
  renderInputPaymentCondition,
  renderInputTvaRegime,
  renderInputIntercommunityTvaNumber,
} from "../CommonInputs";
import InfoContext from "pages/InfoContext";
import { POPUP_STATUS, SIREN_SEARCH_ENTITY } from "constants/constants";
import { getCellErrorHelper } from "utils/helper";
import { useUserContext } from "api/user/UserProvider";
import PropTypes from "prop-types";

const BillingClientPersonalInfoForm = ({
  control,
  register,
  setValue,
  watch,
  errors,
  readOnly = false,
  cellHighlight,
  clearErrors,
  setError,
}) => {
  const { setApiErrorResponse } = useUserContext();

  const {
    lang,
    dataCountry,
    dataTvaRegime,
    dataPaymentCondition,
    searchDavidsonBySiren,
    sirenData,
    sirenErrorMessage,
    setSirenErrorMessage,
    setStatus,
    setCode,
    code,
  } = useContext(InfoContext);

  const {
    company_name,
    postal_code,
    siret,
    city,
    address,
    country,
    numtva,
    siren: sirenValue,
  } = sirenData || {};

  const nationality_id = watch("nationality_id", []);
  const siren = watch("siren", "");

  useEffect(() => {
    const fieldsMap = {
      name: company_name,
      billing_postal_code: postal_code,
      siret: siret,
      billing_city: city,
      billing_address: address,
      billing_country: country,
      intercommunity_tva_number: numtva,
      auxiliary_code: sirenValue
        ? getFormattedAuxilaryCode(sirenValue, true)
        : "",
    };
    Object.entries(fieldsMap).forEach(([field, value]) =>
      setValue(field, !siren ? "" : value)
    );
  }, [sirenValue]);

  const name = watch("name", "");

  useEffect(() => {
    setSirenErrorMessage("");
    setApiErrorResponse(null);
    clearErrors?.("siren");
  }, [siren]);

  useEffect(() => {
    setApiErrorResponse(null);
    clearErrors?.("name");
  }, [name]);

  useEffect(() => {
    if (sirenErrorMessage) {
      setStatus(POPUP_STATUS.ERROR);
      setCode(sirenErrorMessage);
      setError?.("siren", { type: "manual", message: sirenErrorMessage });
    }
  }, [sirenErrorMessage]);

  return (
    dataTvaRegime.length > 0 &&
    dataPaymentCondition.length > 0 && (
      <div className="section-1 billing-client-personalInfo-wrapper">
        <div>
          <Typography
            className="title-3 violet text-uppercase"
            type="h2"
            label={<FormattedMessage id="company" defaultMessage="SOCIÉTÉ" />}
          />
          <div className="billing-client-personalInfo">
            <div className="center-block">
              <div className="line-1">
                <div
                  className={`cell-padding ${getCellErrorHelper({
                    hasValue: watch("name"),
                    hasError: errors?.name,
                    cellHighlight,
                  })}`}
                >
                  {renderCompanyName({
                    control,
                    errors,
                    label: BILLING_CLIENT_FORM_LABELS.name,
                    placeholder: BILLING_CLIENT_FORM_LABELS.name,
                    readOnly,
                    register,
                    name: "name",
                    errorMessage: errors?.name?.message,
                  })}
                </div>
                <div
                  className={`cell-padding ${
                    (!watch("siren") && cellHighlight && "errorCellColor") ||
                    (errors?.siren && "errorCellColor")
                  }`}
                >
                  {renderSiren({
                    control,
                    errors,
                    errorMessage: sirenErrorMessage,
                    label: BILLING_CLIENT_FORM_LABELS.siren,
                    placeholder: BILLING_CLIENT_FORM_LABELS.siren,
                    readOnly,
                    register,
                    value: watch("siren"),
                    nationalityId: nationality_id?.[0]?.value,
                    onClickSearch: (siren) =>
                      searchDavidsonBySiren(
                        siren,
                        SIREN_SEARCH_ENTITY.BILLING_CLIENT
                      ),
                  })}
                </div>
                <div
                  className={`cell-padding ${getCellErrorHelper({
                    hasValue: watch("siret"),
                    hasError: errors?.siret,
                    cellHighlight,
                  })}`}
                >
                  {renderSiret({
                    control,
                    errors,
                    label: BILLING_CLIENT_FORM_LABELS.siret,
                    placeholder: BILLING_CLIENT_FORM_LABELS.siret,
                    readOnly,
                    register,
                    errorMessage: errors?.siret?.message,
                  })}
                </div>
                <div
                  className={`cell-padding ${
                    !watch("auxiliary_code") &&
                    cellHighlight &&
                    "errorCellColor"
                  }`}
                >
                  {renderAuxiliaryCode({
                    control,
                    errors,
                    label: BILLING_CLIENT_FORM_LABELS.auxiliary_code,
                    placeholder: BILLING_CLIENT_FORM_LABELS.auxiliary_code,
                    readOnly,
                    register,
                  })}
                </div>
                <div
                  className={`cell-padding ${
                    watch("nationality_id")?.length === 0 &&
                    cellHighlight &&
                    "errorCellColor"
                  }`}
                >
                  {renderInputNationalityIds({
                    control,
                    errors,
                    label: BILLING_CLIENT_FORM_LABELS.nationality_id,
                    nationalities: dataCountry,
                    placeholder: BILLING_CLIENT_FORM_LABELS.nationality_id,
                    readOnly,
                    register,
                    setValue,
                    value: nationality_id,
                    name: "nationality_id",
                    lang,
                  })}
                </div>
              </div>
              <div className="line-2">
                <div
                  className={`cell-padding ${
                    !watch("payment_condition_id") &&
                    cellHighlight &&
                    "errorCellColor"
                  }`}
                >
                  {renderInputPaymentCondition({
                    control,
                    errors,
                    label: BILLING_CLIENT_FORM_LABELS.payment_condition_id,
                    placeholder:
                      BILLING_CLIENT_FORM_LABELS.payment_condition_id,
                    readOnly,
                    register,
                    setValue,
                    options: dataPaymentCondition?.map((data) => {
                      return {
                        value: data.id,
                        label: data.name,
                      };
                    }),
                  })}
                </div>
                <div
                  className={`cell-padding ${
                    !watch("tva_regime_id") && cellHighlight && "errorCellColor"
                  }`}
                >
                  {renderInputTvaRegime({
                    control,
                    errors,
                    label: BILLING_CLIENT_FORM_LABELS.tva_regime_id,
                    placeholder: BILLING_CLIENT_FORM_LABELS.tva_regime_id,
                    readOnly,
                    register,
                    setValue,
                    options: dataTvaRegime?.map((data) => {
                      return {
                        value: data.id,
                        label: data.name,
                      };
                    }),
                  })}
                </div>
                <div
                  className={`cell-padding ${
                    !watch("intercommunity_tva_number") &&
                    cellHighlight &&
                    "errorCellColor"
                  }`}
                >
                  {renderInputIntercommunityTvaNumber({
                    control,
                    errors,
                    label: BILLING_CLIENT_FORM_LABELS.intercommunity_tva_number,
                    placeholder:
                      BILLING_CLIENT_FORM_LABELS.intercommunity_tva_number,
                    readOnly,
                    register,
                    value: watch("intercommunity_tva_number", ""),
                    setValue,
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  );
};

export default BillingClientPersonalInfoForm;

BillingClientPersonalInfoForm.propTypes = {
  control: PropTypes.object,
  register: PropTypes.func,
  setValue: PropTypes.func,
  errors: PropTypes.object,
  readOnly: PropTypes.bool,
  watch: PropTypes.func,
  cellHighlight: PropTypes.bool,
  clearErrors: PropTypes.func,
  setError: PropTypes.func,
};
